<template>
  <div class="media-video" :style="videoConstraints">
    <div class="media-video__wrapper">
      <template v-if="participant.video">
        <audio ref="audioElement" autoPlay playsInline>
          <track kind="captions" />
        </audio>

        <div
          class="media-video__video"
          :class="[videoFlipped, videoAspectRatio]"
        >
          <video ref="videoElement" autoPlay muted playsInline />
        </div>
      </template>

      <div class="media-video__overlay" :class="videoTrackMuted">
        <template v-if="!participant.video">
          <Avatar
            :src="null"
            :name="participantName"
            size="lg"
            style="margin-top: auto;"
          />
        </template>
        <span class="media-video__overlay-tag">
          <strong
            class="media-video__overlay-tag-name"
            :class="audioTrackMuted"
          >
            {{ participant.local ? "Você" : participantName }}
          </strong>

          <template v-if="participant.audio">
            <Icon
              class="ml-1"
              name="mic-icon"
              :width="12"
              :height="12"
              color="#fff"
            />
          </template>
          <template v-else>
            <Icon
              class="ml-1"
              name="mic-off-icon"
              :width="12"
              :height="12"
              color="#cb6262"
            />
          </template>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "./CustomIcons.vue";
import Avatar from "./Avatar";

export default {
  name: "DailyVideo",
  components: {
    Icon,
    Avatar
  },
  props: {
    participant: {
      type: Object,
      required: true
    },
    flipped: {
      type: Boolean,
      default: false
    },
    hasRemoteParticipants: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      participantName: "Você",
      videoSource: null,
      audioSource: null
    };
  },
  mounted() {
    this.createParticipantVideoTrack(this.participant);
    this.createParticipantAudioTrack(this.participant);
  },
  updated() {
    this.participantName = this.participant?.user_name;
    this.createParticipantVideoTrack(this.participant);
    this.createParticipantAudioTrack(this.participant);
  },
  methods: {
    updateSource(stream, newTrack) {
      if (!stream) return new MediaStream([newTrack]);

      const existingTracks = stream.getTracks();

      if (existingTracks.length > 0) {
        const existingTrack = existingTracks[0];
        if (newTrack.id !== existingTrack.id) {
          stream.removeTrack(existingTrack);
          stream.addTrack(newTrack);
        }
      } else {
        stream.addTrack(newTrack);
      }

      return stream;
    },

    createParticipantVideoTrack(participant) {
      if (!participant?.tracks?.video?.persistentTrack) return;

      const track = participant.tracks.video.persistentTrack;

      this.videoSource = this.updateSource(this.videoSource, track);

      if (this.$refs.videoElement) {
        this.$refs.videoElement.srcObject = this.videoSource;
      }
    },
    createParticipantAudioTrack(participant) {
      if (
        !participant ||
        participant.local ||
        !participant.tracks?.audio?.persistentTrack
      )
        return;

      const track = participant.tracks.audio.persistentTrack;

      this.audioSource = this.updateSource(this.audioSource, track);

      if (this.$refs.audioElement) {
        this.$refs.audioElement.srcObject = this.audioSource;
      }
    }
  },
  computed: {
    videoTrackMuted() {
      return { "media-video-track-muted": !this.participant.video };
    },
    audioTrackMuted() {
      return { "media-audio-track-muted": !this.participant.audio };
    },
    videoFlipped() {
      return { "media-video-flipped": this.flipped };
    },
    videoConstraints() {
      if (this.participant?.local) {
        return !this.hasRemoteParticipants
          ? { maxWidth: "145vmin", height: "75vmin" }
          : { maxWidth: "65vmin" };
      }
      return { maxWidth: "145vmin", height: "75vmin" };
    },
    videoAspectRatio() {
      if (this.participant?.local) {
        return !this.hasRemoteParticipants ? "ratio-16-9" : "ratio-8-5";
      }
      return "ratio-16-9";
    }
  }
};
</script>

<style lang="scss" scoped>
.media-video {
  position: relative;
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  background: #111827;
}

.media-video__wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.media-video__video {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &.ratio-16-9 {
    aspect-ratio: 16/9;
  }
  &.ratio-8-5 {
    aspect-ratio: 8/5;
  }

  &.media-video-flipped video {
    transform: scaleX(-1);
  }
}

.media-video__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 10px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 45%);
  pointer-events: none;

  &.media-video-track-muted {
    background: #111827;
    justify-content: center;
    gap: 12px;
  }
}

.media-video__overlay-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 6px;
  padding: 3px 8px;
}

.media-video__overlay-tag-name {
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
  font-size: 0.75rem;
  font-weight: var(--fw-semibold, 600);
  color: #fff;

  &.media-audio-track-muted {
    color: #f87171;
  }
}
</style>
